/**
 * @file Better Auth client instance.
 *
 * Do not use auth.useSession() directly - use TanStack Query wrappers
 * from lib/queries/session.ts to ensure proper caching and consistency.
 */

import { passkeyClient } from "@better-auth/passkey/client";
import { ac, admin, superadmin, user } from "@repo/core";
import {
  adminClient,
  anonymousClient,
  emailOTPClient,
  organizationClient,
  twoFactorClient,
} from "better-auth/client/plugins";
import type { AccessControl } from "better-auth/plugins/access";
import { createAuthClient } from "better-auth/react";
import { authConfig } from "./auth-config";

const baseURL =
  typeof window !== "undefined"
    ? window.location.origin
    : "http://localhost:5173";

export const auth = createAuthClient({
  baseURL: baseURL + authConfig.api.basePath,
  plugins: [
    adminClient({
      ac: ac as unknown as AccessControl,
      roles: {
        admin,
        superadmin,
        user,
      },
    }),
    anonymousClient(),
    emailOTPClient(),
    organizationClient(),
    passkeyClient(),
    twoFactorClient({
      onTwoFactorRedirect: () => {
        // Redirect to login page with 2FA flag when 2FA verification is needed
        if (typeof window !== "undefined") {
          const currentUrl = new URL(window.location.href);
          const returnTo = currentUrl.searchParams.get("returnTo") || "/";
          window.location.href = `/login?twoFactorRedirect=true&returnTo=${encodeURIComponent(returnTo)}`;
        }
      },
    }),
  ],
});

export type AuthClient = typeof auth;

// Inferred types from configured instance - includes plugin extensions
// $Infer.Session is the full response shape { user, session }
type SessionResponse = typeof auth.$Infer.Session;
export type User = SessionResponse["user"];
export type Session = SessionResponse["session"];
